import { useEffect, useState } from 'react';
import Breadcrumb from '@/components/base/Breadcrumb';

const FORM_ENDPOINT = 'https://readdy.ai/api/form/db6dv5vqhq9pk5mecsrg';

const contactItems = [
  { icon: 'ri-mail-line', label: '邮箱', value: 'hello@changri-time.demo' },
  { icon: 'ri-wechat-line', label: '微信公众号', value: '长日光阴 CINEMA' },
  { icon: 'ri-weibo-line', label: '微博', value: '@长日光阴影视' },
  { icon: 'ri-time-line', label: '回复时间', value: '工作日 10:00 - 18:00' },
];

interface FormState {
  name: string;
  email: string;
  message: string;
  website_alt: string;
}

const emptyForm: FormState = { name: '', email: '', message: '', website_alt: '' };

export default function ContactPage() {
  const [form, setForm] = useState<FormState>(emptyForm);
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  useEffect(() => {
    document.title = '联系我们 - 长日光阴影视推荐平台';
    const desc = document.querySelector('meta[name="description"]');
    if (desc)
      desc.setAttribute(
        'content',
        '联系长日光阴：通过邮箱、社交平台或在线反馈表单告诉我们你的建议与需求，我们会尽快回复。',
      );
  }, []);

  const handleChange = (field: keyof FormState, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (status === 'submitting') return;

    const formEl = event.currentTarget;
    const formData = new FormData(formEl);

    const honeypot = String(formData.get('website_alt') ?? '').trim();
    if (honeypot) {
      setStatus('success');
      setForm(emptyForm);
      return;
    }

    const name = String(formData.get('name') ?? '').trim();
    const email = String(formData.get('email') ?? '').trim();
    const message = String(formData.get('message') ?? '').trim();

    if (!name || !email || !message) {
      setStatus('error');
      setFormError('请完整填写姓名、邮箱和留言内容后再提交。');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setStatus('error');
      setFormError('邮箱格式看起来不太对，请检查后再试。');
      return;
    }
    if (message.length > 500) {
      setStatus('error');
      setFormError('留言内容不能超过 500 个字符。');
      return;
    }

    const payload = new URLSearchParams();
    payload.append('name', name);
    payload.append('email', email);
    payload.append('message', message);

    setStatus('submitting');
    setFormError('');

    try {
      const response = await fetch(FORM_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string }; message?: string } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '';
      const isSpam = /spam/i.test(serverMsg);
      const success = response.ok && parsed?.code === 'OK' && !isSpam;

      if (success) {
        setStatus('success');
        setForm(emptyForm);
      } else {
        setStatus('error');
        setFormError(serverMsg || '提交失败了，请稍后再试。');
      }
    } catch {
      setStatus('error');
      setFormError('网络似乎有点问题，请检查网络后重新提交。');
    }
  };

  return (
    <div className="pb-16">
      <section className="w-full border-b border-background-200 bg-background-100">
        <div className="mx-auto w-full px-4 py-12 md:px-6 md:py-16">
          <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '联系我们' }]} />
          <h1 className="mt-4 font-heading text-3xl font-black text-foreground-950 md:text-4xl">联系我们</h1>
          <p className="mt-4 max-w-3xl text-sm leading-7 text-foreground-700 md:text-base">
            有想推荐的作品、想看的题材，或是对网站有任何建议，欢迎随时和我们聊聊。我们会认真阅读每一条留言。
          </p>
        </div>
      </section>

      <div className="mx-auto grid w-full grid-cols-1 gap-10 px-4 py-12 md:px-6 lg:grid-cols-[1fr_1fr]">
        <div>
          <h2 className="font-heading text-xl font-bold text-foreground-950">联系方式</h2>
          <p className="mt-3 text-sm leading-7 text-foreground-600">
            你也可以通过以下方式找到我们。因站内无账户体系，留言后我们无法回信到站内，烦请留下常用邮箱。
          </p>
          <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
            {contactItems.map((item) => (
              <div key={item.label} className="rounded-lg border border-background-200 bg-background-50 p-5">
                <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary-100 text-primary-700">
                  <i className={`${item.icon} text-lg`} aria-hidden="true" />
                </span>
                <p className="mt-3 text-xs text-secondary-500">{item.label}</p>
                <p className="mt-1 text-sm font-medium text-foreground-900">{item.value}</p>
              </div>
            ))}
          </div>

          <div className="mt-6 rounded-lg border border-accent-200 bg-accent-50 p-5">
            <h3 className="font-heading text-sm font-bold text-accent-900">温馨提醒</h3>
            <p className="mt-2 text-xs leading-relaxed text-accent-900/80">
              长日光阴不提供在线播放、下载或付费服务，因此不会受理资源求档类请求。若涉及内容版权问题，请通过邮箱与我们联系。
            </p>
          </div>
        </div>

        <div>
          <div className="rounded-lg border border-background-200 bg-background-50 p-6 md:p-7">
            <h2 className="font-heading text-xl font-bold text-foreground-950">在线反馈</h2>
            <p className="mt-2 text-sm text-foreground-600">填写下面的表单，我们会尽快回复你。</p>

            {status === 'success' ? (
              <div className="mt-6 flex flex-col items-center rounded-lg border border-dashed border-primary-300 bg-primary-50 px-6 py-12 text-center">
                <span className="flex h-14 w-14 items-center justify-center rounded-full bg-primary-500 text-background-50">
                  <i className="ri-check-line text-2xl" aria-hidden="true" />
                </span>
                <h3 className="mt-4 font-heading text-base font-bold text-foreground-950">感谢你的留言！</h3>
                <p className="mt-2 max-w-sm text-sm text-foreground-600">
                  我们已经收到你的反馈，会认真阅读并尽快回复。期待与你再次相遇在长日光阴。
                </p>
                <button
                  type="button"
                  onClick={() => setStatus('idle')}
                  className="mt-5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-5 py-2.5 text-sm font-medium text-foreground-800 transition-colors hover:border-primary-400 hover:text-primary-600"
                >
                  再写一条
                </button>
              </div>
            ) : (
              <form className="mt-6 space-y-5" onSubmit={handleSubmit} data-readdy-form>
                <div>
                  <label htmlFor="contact-name" className="mb-1.5 block text-sm font-medium text-foreground-800">
                    姓名 <span className="text-accent-600">*</span>
                  </label>
                  <input
                    id="contact-name"
                    name="name"
                    type="text"
                    value={form.name}
                    onChange={(event) => handleChange('name', event.target.value)}
                    placeholder="请输入你的称呼"
                    className="w-full rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-secondary-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
                  />
                </div>

                <div>
                  <label htmlFor="contact-email" className="mb-1.5 block text-sm font-medium text-foreground-800">
                    邮箱 <span className="text-accent-600">*</span>
                  </label>
                  <input
                    id="contact-email"
                    name="email"
                    type="email"
                    value={form.email}
                    onChange={(event) => handleChange('email', event.target.value)}
                    placeholder="用于接收我们的回复"
                    className="w-full rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-secondary-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
                  />
                </div>

                <div>
                  <label htmlFor="contact-message" className="mb-1.5 block text-sm font-medium text-foreground-800">
                    留言 <span className="text-accent-600">*</span>
                  </label>
                  <textarea
                    id="contact-message"
                    name="message"
                    rows={5}
                    maxLength={500}
                    value={form.message}
                    onChange={(event) => handleChange('message', event.target.value.slice(0, 500))}
                    placeholder="想推荐一部作品，或说说你的建议……"
                    className="w-full resize-none rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm leading-6 text-foreground-900 outline-none transition-colors placeholder:text-secondary-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
                  />
                  <p className="mt-1.5 text-right text-xs text-secondary-500">{form.message.length} / 500</p>
                </div>

                <div className="form-hp-field" aria-hidden="true">
                  <label htmlFor="contact-website-alt">请勿填写此栏</label>
                  <input
                    id="contact-website-alt"
                    name="website_alt"
                    type="text"
                    tabIndex={-1}
                    autoComplete="off"
                    readOnly
                    value={form.website_alt}
                    onChange={(event) => handleChange('website_alt', event.target.value)}
                  />
                </div>

                {status === 'error' && formError && (
                  <div className="flex items-start gap-2 rounded-md border border-accent-300 bg-accent-50 px-3.5 py-3 text-sm text-accent-900">
                    <i className="ri-error-warning-line mt-0.5" aria-hidden="true" />
                    <span>{formError}</span>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={status === 'submitting'}
                  className="inline-flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {status === 'submitting' ? (
                    <>
                      <i className="ri-loader-4-line animate-spin" aria-hidden="true" />
                      提交中…
                    </>
                  ) : (
                    <>
                      <i className="ri-send-plane-line" aria-hidden="true" />
                      提交反馈
                    </>
                  )}
                </button>
                <p className="text-center text-xs text-secondary-500">
                  提交即表示你同意我们仅将该信息用于回复你的留言。
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}